import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, Lightbulb, Plus, UserCheck, X } from "lucide-react";
import clsx from "clsx";
import { Button } from "react-aria-components";
import Modal from "ui-kit/src/Modal/Modal";
import { TooltipComponent } from "ui-kit/src/TooltipComponent/TooltipComponent";
import { _ } from "i18n";
import {
	PlayerGradingSubmitType,
	type GPGameTeamResponse,
	type GPPlayerGradingResponse,
	type PlayerGradingSubmitPayload,
	type PlayerGradingSubmitPostPutPayload,
} from "../../../types";
import { formatLastTimeGradedDisplay, formatTeamSheetTeamLabel } from "./playerGradingModalUtils";
import { GRADING_PANEL_WIDTH, gradingPanelPortalRoot } from "./playerGradingPanelLayout";
import { usePlayerGradingPanelLayout } from "./usePlayerGradingPanelLayout";
import { useFullscreenModalContainer } from "../../../useFullscreenModalContainer";
import "./PlayerEventsFilterModal.css";
import "./PlayerGradingModal.css";

const GRADE_VALUES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as const;

const MY_PLAYERS_HREF = "/my-eyeball/my-players";

interface PlayerGradingModalProps {
	playerId: number;
	playerName: string | null;
	gameTeam: GPGameTeamResponse | null;
	gradeId: number | null;
	playerGrading?: GPPlayerGradingResponse | null;
	gradedByScouts?: number | null;
	onSubmit: (payload: PlayerGradingSubmitPayload) => void | Promise<void>;
	toastMessage: (type: string, message: string) => void;
}

interface GradeScaleProps {
	value: number | null;
	onChange: (value: number | null) => void;
	allowToggleOff?: boolean;
	ariaLabelledBy?: string;
}

function GradeScale({ value, onChange, allowToggleOff, ariaLabelledBy }: GradeScaleProps) {
	return (
		<div className="player-grading-modal__scale" role="group" aria-labelledby={ariaLabelledBy}>
			{GRADE_VALUES.map((n) => (
				<button
					key={n}
					type="button"
					className={clsx("player-grading-modal__grade-btn", value === n && "player-grading-modal__grade-btn--selected")}
					aria-pressed={value === n}
					onClick={() => {
						if (allowToggleOff && value === n) {
							onChange(null);
						} else {
							onChange(n);
						}
					}}
				>
					{n}
				</button>
			))}
		</div>
	);
}

export function PlayerGradingModal({
	playerId,
	playerName,
	gameTeam,
	gradeId,
	playerGrading,
	gradedByScouts,
	onSubmit,
	toastMessage,
}: PlayerGradingModalProps) {
	const triggerRef = useRef<HTMLDivElement>(null);
	const panelRef = useRef<HTMLDivElement>(null);
	const wasPanelOpenRef = useRef(false);
	const [isOpen, setIsOpen] = useState(false);
	const [performanceGrade, setPerformanceGrade] = useState<number | null>(null);
	const [potentialGrade, setPotentialGrade] = useState<number | null>(null);
	const [note, setNote] = useState("");
	const [isSaving, setIsSaving] = useState(false);
	const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);

	const isGraded = playerGrading?.performanceGrade != null;
	const hasExistingGradingRow = gradeId != null;
	const canRemoveGrade = hasExistingGradingRow;
	const fullscreenModalContainer = useFullscreenModalContainer();
	const sidebarEl = typeof document !== "undefined" ? (document.getElementById("video-sidebar") ?? undefined) : undefined;
	const deleteModalPortalContainer = fullscreenModalContainer ?? sidebarEl;

	const teamLabel = useMemo(() => formatTeamSheetTeamLabel(gameTeam), [gameTeam]);
	const lastTimeGradedLine = useMemo(() => {
		const lastGradedAt = playerGrading?.lastTimeGraded
			? formatLastTimeGradedDisplay(playerGrading.lastTimeGraded)
			: "";
		return lastGradedAt ? `${_("gamePage.playerGrading.lastTimeGraded")} ${lastGradedAt}` : null;
	}, [playerGrading?.lastTimeGraded]);
	const showScoutGradingMeta = !!gradedByScouts;
	const gradedByScoutsLine = useMemo(() => {
		if (!showScoutGradingMeta) {
			return null;
		}
		return gradedByScouts === 1
			? _("gamePage.playerGrading.gradedByScoutsSingular")
			: _("gamePage.playerGrading.gradedByScoutsPlural", { count: String(gradedByScouts) });
	}, [showScoutGradingMeta, gradedByScouts]);

	const panelContentLayoutKey = useMemo(
		() =>
			[
				performanceGrade,
				potentialGrade,
				note,
				playerName ?? "",
				teamLabel,
				lastTimeGradedLine ?? "",
				gradedByScoutsLine ?? "",
				canRemoveGrade,
			].join("\u0000"),
		[
			performanceGrade,
			potentialGrade,
			note,
			playerName,
			teamLabel,
			lastTimeGradedLine,
			gradedByScoutsLine,
			canRemoveGrade,
		],
	);

	const { panelLayout, invalidatePanelSize } = usePlayerGradingPanelLayout(
		isOpen,
		triggerRef,
		panelRef,
		panelContentLayoutKey,
	);

	useEffect(() => {
		if (!isOpen) {
			return;
		}
		const onPointerDown = (e: PointerEvent) => {
			const t = e.target as Node;
			if (panelRef.current?.contains(t) || triggerRef.current?.contains(t)) {
				return;
			}
			setIsOpen(false);
		};
		document.addEventListener("pointerdown", onPointerDown, true);
		return () => document.removeEventListener("pointerdown", onPointerDown, true);
	}, [isOpen]);

	useLayoutEffect(() => {
		if (!isOpen) {
			wasPanelOpenRef.current = false;
			return;
		}
		if (!wasPanelOpenRef.current && panelRef.current) {
			panelRef.current.focus({ preventScroll: true });
			wasPanelOpenRef.current = true;
		}
	}, [isOpen, panelLayout]);

	const noteFieldId = `player-grading-note-${playerId}`;
	const perfId = `player-grading-performance-${playerId}`;
	const potId = `player-grading-potential-${playerId}`;
	const gradingTitleId = `player-grading-dialog-title-${playerId}`;

	const gradingTeamSheetButtonTooltip = isGraded
		? _("gamePage.playerGrading.teamSheetButtonTooltipGraded")
		: _("gamePage.playerGrading.teamSheetButtonTooltipUngraded");

	const setPopoverOpen = (open: boolean) => {
		setIsOpen(open);
		invalidatePanelSize();
		if (open) {
			setPerformanceGrade(playerGrading?.performanceGrade ?? null);
			setPotentialGrade(playerGrading?.potentialGrade ?? null);
			setNote(playerGrading?.note ?? "");
		}
	};

	const closeConfirmDelete = () => {
		if (!isDeleting) {
			setConfirmDeleteOpen(false);
		}
	};

	const handleSave = async (type: PlayerGradingSubmitType.Post | PlayerGradingSubmitType.Put) => {
		if (performanceGrade === null) {
			toastMessage("error", _("gamePage.playerGrading.performanceGradeRequired"));
			return;
		}

		if (type === PlayerGradingSubmitType.Put && gradeId == null) {
			toastMessage("error", _("gamePage.playerGrading.updateGradeError"));
			return;
		}

		const payload: PlayerGradingSubmitPostPutPayload = {
			type,
			id: type === PlayerGradingSubmitType.Post ? playerId : gradeId!,
			performanceGrade,
			potentialGrade,
			note: note.trim() === "" ? null : note.trim(),
		};

		setIsSaving(true);
		try {
			await Promise.resolve(onSubmit(payload));
			setPopoverOpen(false);
		} catch {
			// Swallow: callers use void handleSave; parent onSubmit (e.g. mutateAsync) may use onError for toasts
		} finally {
			setIsSaving(false);
		}
	};

	const handleConfirmDelete = async () => {
		if (gradeId == null) {
			return;
		}

		setIsDeleting(true);
		try {
			await Promise.resolve(
				onSubmit({
					type: PlayerGradingSubmitType.Delete,
					id: gradeId,
				}),
			);
			setConfirmDeleteOpen(false);
			setPopoverOpen(false);
		} catch {
			// Swallow: void handleConfirmDelete; parent onSubmit onError for toasts
		} finally {
			setIsDeleting(false);
		}
	};

	const portalRoot = gradingPanelPortalRoot();
	const panelShell =
		isOpen && panelLayout && portalRoot ? (
			<>
				<div
					className="player-grading-panel__backdrop"
					aria-hidden
					onPointerDown={(e) => {
						if (e.target === e.currentTarget) {
							setPopoverOpen(false);
						}
					}}
				/>
				<div
					ref={panelRef}
					role="dialog"
					aria-modal="true"
					aria-labelledby={gradingTitleId}
					className={clsx("modal-overlay", "player-grading-popover", "player-grading-panel")}
					style={{
						width: GRADING_PANEL_WIDTH,
						top: panelLayout.top,
						left: panelLayout.left,
						...(panelLayout.height == null
							? { height: "auto", maxHeight: "none" }
							: { height: panelLayout.height, maxHeight: panelLayout.height }),
					}}
					tabIndex={-1}
					onKeyDown={(e) => {
						if (e.key === "Escape") {
							e.stopPropagation();
							setPopoverOpen(false);
						}
					}}
				>
					<div className="modal-header player-grading-modal__popover-header">
						<div className="player-grading-modal__popover-header-text">
							<div id={gradingTitleId}>{_("gamePage.playerGrading.title")}</div>
							{gradedByScoutsLine ? (
								<a
									className="player-grading-modal__graded-by-scouts-link"
									href={MY_PLAYERS_HREF}
									target="_blank"
									rel="noopener noreferrer"
								>
									{gradedByScoutsLine}
								</a>
							) : null}
							{lastTimeGradedLine ? (
								<div className="player-grading-modal__popover-header-last-graded">{lastTimeGradedLine}</div>
							) : null}
						</div>
						<span className="modal-header__close" onClick={() => setPopoverOpen(false)}>
							<X size={16} />
						</span>
					</div>
					<div className="modal-body player-grading-modal">
						<div className="player-grading-modal__scroll">
							<div className="player-grading-modal__player-block">
								{playerName ? <div className="player-grading-modal__player-name">{playerName}</div> : null}
								{teamLabel ? <div className="player-grading-modal__team-name">{teamLabel}</div> : null}
							</div>
							<section className="player-grading-modal__section" aria-labelledby={perfId}>
								<div className="player-grading-modal__field-label" id={perfId}>
									{_("gamePage.playerGrading.performanceGrade")}
									<span className="player-grading-modal__required" aria-hidden>
										*
									</span>
								</div>
								<GradeScale value={performanceGrade} onChange={setPerformanceGrade} ariaLabelledBy={perfId} />
							</section>
							<section className="player-grading-modal__section" aria-labelledby={potId}>
								<div className="player-grading-modal__field-label" id={potId}>
									{_("gamePage.playerGrading.potentialGrade")}
									<span className="player-grading-modal__field-label--muted" aria-hidden>
										{_("gamePage.playerGrading.fieldOptional")}
									</span>
								</div>
								<GradeScale value={potentialGrade} onChange={setPotentialGrade} allowToggleOff ariaLabelledBy={potId} />
							</section>
							<section className="player-grading-modal__section">
								<label className="player-grading-modal__field-label" htmlFor={noteFieldId}>
									{_("gamePage.playerGrading.noteLabel")}
									<span className="player-grading-modal__field-label--muted" aria-hidden>
										{_("gamePage.playerGrading.fieldOptional")}
									</span>
								</label>
								<p className="player-grading-modal__note-helper">{_("gamePage.playerGrading.noteHelper")}</p>
								<textarea
									id={noteFieldId}
									className="player-grading-modal__textarea"
									rows={4}
									value={note}
									onChange={(e) => setNote(e.target.value)}
									placeholder={_("gamePage.playerGrading.notePlaceholder")}
								/>
								<div className="player-grading-modal__footer-hint">
									<Lightbulb className="player-grading-modal__footer-hint-icon" color="#F0EE42" size={16} aria-hidden />
									<span>{_("gamePage.playerGrading.footerHint")}</span>
								</div>
							</section>
						</div>

						<div className="player-grading-modal__actions-container">
							<div className="modal-controls player-grading-modal__actions">
								<Button
									className="modal-button"
									onPress={() => setPopoverOpen(false)}
									isDisabled={isSaving || isDeleting}
								>
									{_("gamePage.playerGrading.cancel")}
								</Button>
								<Button
									className={clsx("modal-button", "playEvents", "player-grading-modal__submit")}
									onPress={() =>
										void handleSave(
											hasExistingGradingRow ? PlayerGradingSubmitType.Put : PlayerGradingSubmitType.Post,
										)
									}
									isDisabled={performanceGrade === null || isSaving || isDeleting}
								>
									{_("gamePage.playerGrading.save")}
								</Button>
							</div>
							{canRemoveGrade ? (
								<Button
									className="remove-grade-btn"
									onPress={() => {
										setConfirmDeleteOpen(true);
										setPopoverOpen(false);
									}}
									isDisabled={isSaving || isDeleting}
								>
									{_("gamePage.playerGrading.removeGrade")}
								</Button>
							) : null}
						</div>
					</div>
				</div>
			</>
		) : null;

	return (
		<div>
			<div
				ref={triggerRef}
				className="team-sheet-actions__add-grade-wrap"
				aria-label={isGraded ? _("gamePage.playerGrading.triggerAriaGraded") : _("gamePage.playerGrading.triggerAria")}
			>
				<TooltipComponent title={gradingTeamSheetButtonTooltip} variant="dark" delay={250}>
					<div
						className={clsx("team-sheet-actions__add-grade", isGraded && "team-sheet-actions__add-grade--graded")}
						onClick={(e) => {
							e.stopPropagation();
							setPopoverOpen(!isOpen);
						}}
					>
						{isGraded ? (
							<Check className="team-sheet-actions__add-grade-check" size={12} color="#06A983" strokeWidth={2.5} />
						) : (
							<Plus className="team-sheet-actions__add-grade-plus" size={12} color="#000938" strokeWidth={2.5} />
						)}
					</div>
				</TooltipComponent>
				{showScoutGradingMeta ? (
					<span className="team-sheet-actions__scout-indicator" aria-hidden>
						<UserCheck className="team-sheet-actions__scout-indicator-icon" size={10} color="#fff" strokeWidth={2.5} />
					</span>
				) : null}
			</div>
			{panelShell && portalRoot ? createPortal(panelShell, portalRoot) : null}

			<Modal
				show={confirmDeleteOpen}
				onClose={closeConfirmDelete}
				title={_("gamePage.playerGrading.deleteConfirmTitle")}
				size="xs"
				UNSTABLE_portalContainer={deleteModalPortalContainer}
			>
				<p className="player-grading-modal__delete-confirm-body">{_("gamePage.playerGrading.deleteConfirmBody")}</p>
				<div className="modal-controls player-grading-modal__delete-confirm-actions">
					<Button className="modal-button" onPress={closeConfirmDelete} isDisabled={isDeleting}>
						{_("gamePage.playerGrading.deleteConfirmCancel")}
					</Button>
					<Button
						className={clsx("modal-button", "player-grading-modal__delete-confirm-remove")}
						onPress={() => void handleConfirmDelete()}
						isDisabled={isDeleting}
					>
						{_("gamePage.playerGrading.deleteConfirmRemove")}
					</Button>
				</div>
			</Modal>
		</div>
	);
}
